import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CardMedia from "@mui/material/CardMedia";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Header from "../core/Header";
import Footer from "../core/Footer";
import sampleProducts from "../data/products";
import { smdProducts } from "../data/smdProducts";
import { useTranslation } from "../utils/i18n";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, resolve } = useTranslation();

  const product = sampleProducts.find((p) => p.slug === slug);

  const rawCategory = product?.category;
  const translatedCategory = rawCategory ? t(`categoryLabels.${rawCategory}`) : null;
  const productLabel = (translatedCategory && translatedCategory !== `categoryLabels.${rawCategory}`)
    ? translatedCategory
    : rawCategory || t("productDetail.labels.default");
  const productsBasePath = "/products";
  const mainImage = product?.image || "/images/produkt.png";
  const productTitle = resolve(product?.title ?? product?.name);
  const productDescription = resolve(
    product?.productDescription ?? product?.description ?? product?.shortDescription
  );
  const [activeImage, setActiveImage] = useState(mainImage);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const pdfs = product?.pdfs || [];

  useEffect(() => {
    setActiveImage(mainImage);
  }, [mainImage]);

  const galleryItems = useMemo(() => {
    if (Array.isArray(product?.gallery) && product.gallery.length > 0) {
      return product.gallery.map((image) => ({ image }));
    }
    return [{ image: mainImage }, { image: mainImage }, { image: mainImage }];
  }, [mainImage, product?.gallery]);

  const features = product?.features || product?.generalData || [];

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const oppositeCategory = product.category === "Mottagare" ? "Sändare" : product.category === "Sändare" ? "Mottagare" : null;
    if (!oppositeCategory) return [];
    return smdProducts.filter((p) => p.category === oppositeCategory && p.slug !== product.slug);
  }, [product]);

  const relatedHeading = product?.category === "Mottagare"
    ? t("productDetail.relatedTransmitters")
    : t("productDetail.relatedReceivers");

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#F6F7F9", display: "flex", flexDirection: "column" }}>
      <Header />

      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 10.5, md: 13 },
          pb: { xs: 5, md: 8 },
        }}
      >
        {!product ? (
          <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: "transparent", borderRadius: 2 }}>
            <Typography variant="h6">{t("productDetail.notFound")}</Typography>
          </Box>
        ) : (
          <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1.75 }}>
              <Link
                component={RouterLink}
                to="/"
                underline="none"
                sx={{ color: "#4b4b4b" }}
              >
                {t("productDetail.breadcrumbHome")}
              </Link>
              <Link
                component={RouterLink}
                to={productsBasePath}
                state={{ restoreScroll: true }}
                underline="none"
                sx={{ color: "#4b4b4b" }}
              >
                {t("productDetail.breadcrumbProducts")}
              </Link>
              <Typography aria-current="page" sx={{ color: "#FF6A00" }}>
                {productTitle}
              </Typography>
            </Breadcrumbs>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(0, 1.05fr) minmax(360px, 0.95fr)",
                },
                gap: { xs: 3, md: 4 },
                alignItems: "start",
                mt: 7,
              }}
            >
              {/* Image panel */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 1,
                  pb: { xs: 2, md: 2.5 },
                }}
              >
                <Box
                  onClick={() => {
                    const idx = galleryItems.findIndex((item) => item.image === activeImage);
                    setLightboxIndex(idx >= 0 ? idx : 0);
                    setLightboxOpen(true);
                  }}
                  sx={{
                    borderRadius: 2,
                    minHeight: { xs: 320, md: 520 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    bgcolor: "transparent",
                    cursor: "zoom-in",
                  }}
                >
                  <CardMedia
                    component="img"
                    image={activeImage}
                    alt={productTitle}
                    sx={{
                      width: "100%",
                      maxWidth: { xs: 280, md: 420 },
                      maxHeight: { xs: 280, md: 420 },
                      objectFit: "contain",
                      px: 2,
                      py: 3,
                    }}
                  />
                </Box>

                <Dialog
                  open={lightboxOpen}
                  onClose={() => setLightboxOpen(false)}
                  fullScreen
                  PaperProps={{ sx: { bgcolor: "#0a0a0a" } }}
                >
                  <IconButton
                    onClick={() => setLightboxOpen(false)}
                    sx={{ position: "absolute", top: 16, right: 16, color: "#fff", zIndex: 2, bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                  >
                    <CloseIcon />
                  </IconButton>

                  {galleryItems.length > 1 && (
                    <>
                      <IconButton
                        onClick={() => setLightboxIndex((i) => i - 1)}
                        disabled={lightboxIndex === 0}
                        sx={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#fff", zIndex: 2, bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" }, "&.Mui-disabled": { opacity: 0.25 } }}
                      >
                        <ArrowBackIosNewIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setLightboxIndex((i) => i + 1)}
                        disabled={lightboxIndex === galleryItems.length - 1}
                        sx={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "#fff", zIndex: 2, bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" }, "&.Mui-disabled": { opacity: 0.25 } }}
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </>
                  )}

                  <DialogContent sx={{ p: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Box
                      component="img"
                      src={galleryItems[lightboxIndex]?.image || activeImage}
                      alt={productTitle}
                      sx={{ maxWidth: "100%", maxHeight: "100vh", objectFit: "contain" }}
                    />
                  </DialogContent>
                </Dialog>

                {galleryItems.length > 1 && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    {galleryItems.map((item, index) => {
                      const isActive = activeImage === item.image;
                      return (
                        <Box
                          key={`${item.image}-${index}`}
                          onClick={() => setActiveImage(item.image)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setActiveImage(item.image);
                            }
                          }}
                          sx={{
                            cursor: "pointer",
                            borderRadius: 1.5,
                            bgcolor: "#fff",
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 0.75,
                            outline: "none",
                            border: isActive ? "2px solid #FF6A00" : "2px solid transparent",
                            transition: "border-color 150ms ease",
                            "&:hover": { border: "2px solid rgba(255,106,0,0.45)" },
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={item.image}
                            alt={`${productTitle} ${index + 1}`}
                            sx={{ objectFit: "contain", maxHeight: 56 }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* Info panel */}
              <Box sx={{ borderRadius: 2, p: { xs: 2.5, md: 4 } }}>
                <Typography
                  sx={{
                    color: "#FF6A00",
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  {productLabel}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.04em",
                    lineHeight: 1.05,
                    color: "#0B0F13",
                    fontSize: { xs: "2.1rem", md: "3rem" },
                    mb: 2,
                  }}
                >
                  {productTitle}
                </Typography>

                <Typography
                  sx={{
                    color: "#475467",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.98rem", md: "1.03rem" },
                    mb: 2.5,
                  }}
                >
                  {productDescription}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mb: 3 }}>
                  {features.map((feature, index) => {
                    const isObj = typeof feature === "object" && feature !== null;
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          py: 3,
                          borderBottom:
                            index < features.length - 1
                              ? "1px solid rgba(30, 26, 24, 0.12)"
                              : "none",
                        }}
                      >
                        <CheckCircleOutlineIcon
                          sx={{ color: "#FF6A00", mt: 0.3, fontSize: 22, flexShrink: 0 }}
                        />
                        <Box>
                          {isObj ? (
                            <>
                              <Typography
                                sx={{ fontWeight: 700, color: "#0B0F13", lineHeight: 1.4, fontSize: { xs: "1rem", md: "1.04rem" } }}
                              >
                                {resolve(feature.title)}
                              </Typography>
                              <Typography
                                sx={{ color: "#475467", lineHeight: 1.6, fontSize: { xs: "0.9rem", md: "0.95rem" }, mt: 0.4 }}
                              >
                                {resolve(feature.description)}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              sx={{ color: "#1D2129", lineHeight: 1.6, fontSize: { xs: "1.02rem", md: "1.06rem" } }}
                            >
                              {resolve(feature)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* Related products */}
            {relatedProducts.length > 0 && (
              <Box sx={{ mt: { xs: 4, md: 5 } }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", md: "1.25rem" }, color: "#0B0F13", mb: 2 }}>
                  {relatedHeading}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
                    gap: 2,
                  }}
                >
                  {relatedProducts.map((rp) => (
                    <Box
                      key={rp.slug}
                      onClick={() => navigate(`/products/${rp.slug}`)}
                      sx={{
                        bgcolor: "#fff",
                        border: "1px solid #E3E6EB",
                        borderRadius: 2,
                        p: 2,
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        transition: "border-color 150ms ease, box-shadow 150ms ease",
                        "&:hover": { borderColor: "#FF6A00", boxShadow: "0 4px 12px rgba(255,106,0,0.15)" },
                      }}
                    >
                      <Box
                        component="img"
                        src={rp.image}
                        alt={resolve(rp.title)}
                        sx={{ width: "100%", maxHeight: 100, objectFit: "contain" }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#0B0F13", lineHeight: 1.3 }}>
                          {resolve(rp.title)}
                        </Typography>
                        <ArrowForwardIcon sx={{ color: "#FF6A00", fontSize: 18, flexShrink: 0 }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ mt: { xs: 2.5, md: 3 }, borderTop: "1px solid rgba(30, 26, 24, 0.18)" }} />
            <Box
              sx={{
                mt: { xs: 3, md: 4 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.25,
              }}
            >
              {pdfs.length === 1 && (
                <Button
                  variant="contained"
                  component="a"
                  href={pdfs[0].url}
                  download
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  sx={{
                    bgcolor: "#FF6A00",
                    color: "#fff",
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.1,
                    px: 2.75,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#E05500", boxShadow: "none" },
                  }}
                >
                  {t("productDetail.downloadLabel")}
                </Button>
              )}

              {pdfs.length > 1 && (
                <Button
                  variant="contained"
                  onClick={() => setPdfDialogOpen(true)}
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  sx={{
                    bgcolor: "#FF6A00",
                    color: "#fff",
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.1,
                    px: 2.75,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#E05500", boxShadow: "none" },
                  }}
                >
                  {t("productDetail.downloadLabel")}
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={() => navigate("/contact")}
                startIcon={<MailOutlineIcon />}
                sx={{
                  borderColor: "rgba(30, 26, 24, 0.22)",
                  color: "#1D2129",
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.1,
                  px: 2.75,
                  bgcolor: "transparent",
                }}
              >
                {t("productDetail.contact")}
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate("/contact", { state: { subject: product?.title?.sv ?? product?.name?.sv ?? productTitle } })}
                startIcon={<RequestQuoteOutlinedIcon />}
                sx={{
                  borderColor: "rgba(30, 26, 24, 0.22)",
                  color: "#1D2129",
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.1,
                  px: 2.75,
                  bgcolor: "transparent",
                }}
              >
                {t("productDetail.quote")}
              </Button>
            </Box>

            {/* PDF selection dialog */}
            <Dialog
              open={pdfDialogOpen}
              onClose={() => setPdfDialogOpen(false)}
              maxWidth="xs"
              fullWidth
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <Box sx={{ px: 3, pt: 3, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#0B0F13" }}>
                  {t("productDetail.selectDatasheet")}
                </Typography>
                <IconButton onClick={() => setPdfDialogOpen(false)} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <DialogContent sx={{ pt: 0.5, pb: 2 }}>
                <List disablePadding>
                  {pdfs.map((pdf, i) => (
                    <ListItemButton
                      key={i}
                      component="a"
                      href={pdf.url}
                      download
                      onClick={() => setPdfDialogOpen(false)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.75,
                        border: "1px solid #E3E6EB",
                        gap: 1.5,
                        "&:hover": { bgcolor: "rgba(255,106,0,0.06)", borderColor: "rgba(255,106,0,0.3)" },
                      }}
                    >
                      <FileDownloadOutlinedIcon sx={{ color: "#FF6A00", fontSize: 20, flexShrink: 0 }} />
                      <ListItemText
                        primary={resolve(pdf.label)}
                        primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 600, color: "#0B0F13" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </DialogContent>
            </Dialog>
          </>
        )}
      </Box>
      <Footer />
    </Box>
  );
}
